import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  FeatureFlags,
  INSURANCE_FAILED_ERROR_CODE,
  INSURANCE_MINIMUM_AMOUNT_ERROR_CODE,
  INSURANCE_MINIMUM_AMOUNT_ERROR_CODES,
} from '@core/constants';
import { SKIP_INSURANCE_PROMPT } from '@core/services/base.service';
import { ContentManagementService } from '@core/services/content-management/content-management.service';
import { FeatureFlagService } from '@core/services/feature-flag/feature-flag.service';
import { calculateCommodityTotalValue } from '@core/utils/calculate-commodity-total-value.util';
import { getJsonBlobFromFormData } from '@core/utils/get-json-blob-from-form-data.util';
import { readJsonFromBlob } from '@core/utils/read-json-from-blob.util';
import { InsuranceErrorComponent } from '@shared/components/insurance-error/insurance-error.component';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable, Observer, Subscription } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class InsuranceInterceptorHandler {
  ref: DynamicDialogRef<any> | null | undefined;

  private readonly dialogService: DialogService = inject(DialogService);
  private readonly contentManagementService = inject(ContentManagementService);
  private readonly featureFlagService: FeatureFlagService =
    inject(FeatureFlagService);

  shouldHandle(req: HttpRequest<any>): boolean {
    const isHybridParcelRequest = this.isHybridParcelInsuranceEndpoint(req.url);
    const insuranceEnabled = this.featureFlagService.featureFlagEnabled(
      isHybridParcelRequest
        ? FeatureFlags.ENABLE_HYBRID_PARCEL_INSURANCE
        : FeatureFlags.ENABLE_INSURANCE
    );
    const supportedMethod = isHybridParcelRequest
      ? req.method === 'PATCH'
      : req.method === 'POST' || req.method === 'PUT';

    return (
      insuranceEnabled && supportedMethod && this.isInsuranceEndpoint(req.url)
    );
  }

  handleRequest(
    authReq: HttpRequest<any>,
    originalReq: HttpRequest<any>,
    next: HttpHandler,
    handleWithError: (
      authReq: HttpRequest<any>,
      originalReq: HttpRequest<any>,
      next: HttpHandler
    ) => Observable<HttpEvent<any>>
  ): Observable<HttpEvent<any>> {
    if (authReq.context.get(SKIP_INSURANCE_PROMPT)) {
      return handleWithError(authReq, originalReq, next);
    }

    if (authReq.body instanceof FormData) {
      const formData = authReq.body as FormData;
      const jsonBlob = getJsonBlobFromFormData(formData);

      if (jsonBlob) {
        return this.handleFormDataInsurance(
          authReq,
          originalReq,
          next,
          jsonBlob,
          handleWithError
        );
      }
    }

    const { shouldPrompt, totalValue } =
      this.shouldShowInsurancePrompt(authReq);

    if (shouldPrompt) {
      return this.openInsurancePromptAndMaybeContinue(
        authReq,
        next,
        totalValue
      );
    }

    return handleWithError(authReq, originalReq, next);
  }

  handleError(
    req: HttpRequest<any>,
    next: HttpHandler,
    error: HttpErrorResponse
  ): Observable<HttpEvent<any>> {
    const isMinimumAmountError = this.isMinimumInsuranceAmountError(
      error,
      req.url
    );

    return new Observable((observer) => {
      const openDialog = (requestMinimumAmount?: unknown) => {
        const errorPayload = this.getInsuranceErrorPayload(error);

        this.ref = this.dialogService.open(InsuranceErrorComponent, {
          modal: true,
          closable: false,
          closeOnEscape: false,
          width: '460px',
          data: {
            option: 'Insurance',
            message: isMinimumAmountError
              ? this.getMinimumAmountErrorMessage(error, requestMinimumAmount)
              : (errorPayload?.errorMessage ?? errorPayload?.message),
            title: isMinimumAmountError
              ? 'Total Insurance Amount Too Low'
              : 'Insurance Could Not Be Created',
          },
        });

        this.ref?.onClose.subscribe((result) => {
          if (result === 'CONTINUE_WITHOUT_OPTION') {
            this.resendWithoutInsurance(req, next, observer);
          } else {
            if (isMinimumAmountError) {
              observer.complete();
              return;
            }

            observer.error(
              this.getInsuranceErrorCode(error) || INSURANCE_FAILED_ERROR_CODE
            );
          }
        });
      };

      if (isMinimumAmountError) {
        this.getMinimumAmountFromRequest(req, openDialog);
      } else {
        openDialog();
      }
    });
  }

  isHandledError(error: any, url: string): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }

    const errorCode = this.getInsuranceErrorCode(error);

    if (this.isMinimumInsuranceAmountError(error, url)) {
      return true;
    }

    return error.status === 500 && errorCode === INSURANCE_FAILED_ERROR_CODE;
  }

  resendWithoutInsurance(
    req: HttpRequest<any>,
    next: HttpHandler,
    observer: Observer<HttpEvent<any>>
  ): void {
    if (req.body instanceof FormData) {
      const originalFormData = req.body;
      const jsonBlob = getJsonBlobFromFormData(originalFormData);

      if (jsonBlob) {
        readJsonFromBlob(
          jsonBlob,
          (requestDto) => {
            try {
              this.removeInsuranceFromRequestBody(requestDto);

              const updatedJson = JSON.stringify(requestDto);

              const updatedBlob = new Blob([updatedJson], {
                type: 'application/json',
              });

              const newFormData = new FormData();

              originalFormData.forEach((value, key) => {
                if (key === 'requestDTO') {
                  newFormData.append(key, updatedBlob, 'blob');
                } else {
                  newFormData.append(key, value as any);
                }
              });

              const modifiedReq = req.clone({ body: newFormData });

              this.forwardObservable(next.handle(modifiedReq), observer);
            } catch (e) {
              observer.error('Failed to modify request payload (insurance).');
            }
          },
          () => {
            observer.error('Failed to read request payload (insurance).');
          }
        );

        return;
      }
    }

    if (
      req.body &&
      typeof req.body === 'object' &&
      !(req.body instanceof FormData)
    ) {
      const newBody: any = { ...req.body };

      this.removeInsuranceFromRequestBody(newBody);

      const modifiedReq = req.clone({ body: newBody });

      this.forwardObservable(next.handle(modifiedReq), observer);

      return;
    }

    observer.error('Unsupported request body format for insurance removal.');
  }

  shouldShowInsurancePromptFromBody(body: any): {
    shouldPrompt: boolean;
    totalValue: number;
  } {
    if (!body || typeof body !== 'object') {
      return { shouldPrompt: false, totalValue: 0 };
    }

    const totalValue = calculateCommodityTotalValue(
      body.commodities ||
        body.shipmentCommodities ||
        body.internationalShipmentDetails?.commodities
    );
    const hasInsurance = !!body.insurance;

    return {
      shouldPrompt: totalValue >= 5000 && !hasInsurance,
      totalValue,
    };
  }

  shouldShowInsurancePrompt(req: HttpRequest<any>): {
    shouldPrompt: boolean;
    totalValue: number;
  } {
    const body: any = req.body;

    if (!body || typeof body !== 'object' || body instanceof FormData) {
      return { shouldPrompt: false, totalValue: 0 };
    }

    return this.shouldShowInsurancePromptFromBody(body);
  }

  private isInsuranceEndpoint(url: string): boolean {
    return (
      url.endsWith('/express-shipments/with-supporting-documents') ||
      url.endsWith('/express-shipments/quick/with-supporting-documents') ||
      url.endsWith('/express-shipments/update') ||
      url.endsWith('/cold-chains') ||
      this.isHybridParcelInsuranceEndpoint(url)
    );
  }

  private isHybridParcelInsuranceEndpoint(url: string): boolean {
    return /\/multileg-shipments\/hybrid-parcels\/[^/?]+\/details(?:\?.*)?$/.test(
      url
    );
  }

  private handleFormDataInsurance(
    authReq: HttpRequest<any>,
    originalReq: HttpRequest<any>,
    next: HttpHandler,
    jsonBlob: Blob,
    handleWithError: (
      authReq: HttpRequest<any>,
      originalReq: HttpRequest<any>,
      next: HttpHandler
    ) => Observable<HttpEvent<any>>
  ): Observable<HttpEvent<any>> {
    return new Observable<HttpEvent<any>>((observer) => {
      let innerSub: Subscription | null = null;

      const forwardError = () => {
        if (innerSub) {
          innerSub.unsubscribe();
        }
        innerSub = this.forwardObservable(
          handleWithError(authReq, originalReq, next),
          observer
        );
      };

      readJsonFromBlob(
        jsonBlob,
        (json) => {
          const { shouldPrompt, totalValue } =
            this.shouldShowInsurancePromptFromBody(json);

          if (shouldPrompt) {
            if (innerSub) {
              innerSub.unsubscribe();
            }
            innerSub = this.forwardObservable(
              this.openInsurancePromptAndMaybeContinue(
                authReq,
                next,
                totalValue
              ),
              observer
            );
          } else {
            forwardError();
          }
        },
        () => {
          forwardError();
        }
      );

      return () => {
        if (innerSub) {
          innerSub.unsubscribe();
        }
      };
    });
  }

  private openInsurancePromptAndMaybeContinue(
    req: HttpRequest<any>,
    next: HttpHandler,
    totalValue: number
  ): Observable<HttpEvent<any>> {
    return new Observable<HttpEvent<any>>((observer) => {
      this.ref = this.dialogService.open(InsuranceErrorComponent, {
        modal: true,
        closable: false,
        closeOnEscape: false,
        width: '460px',
        data: {
          option: 'Insurance',
          message:
            'Adding insurance provides coverage in case of loss or damage. You can continue without insurance or add it now before completing the shipment.',
          title: 'Would you like to protect your shipment with insurance?',
        },
      });

      this.ref?.onClose.subscribe((result) => {
        if (result === 'CONTINUE_WITHOUT_OPTION') {
          this.forwardObservable(next.handle(req), observer);
        } else {
          observer.error(INSURANCE_FAILED_ERROR_CODE);
        }
      });
    });
  }

  private forwardObservable(
    source$: Observable<HttpEvent<any>>,
    observer: Observer<HttpEvent<any>>
  ): Subscription {
    return source$.subscribe({
      next: (event) => observer.next(event),
      error: (err) => observer.error(err),
      complete: () => observer.complete(),
    });
  }

  private getMinimumAmountErrorMessage(
    error: HttpErrorResponse,
    requestMinimumAmount?: unknown
  ): string {
    const errorPayload = this.getInsuranceErrorPayload(error);
    const payloadMinimumAmount = this.getMinimumAmountFromPayload(errorPayload);
    const minimumAmount = this.hasMinimumAmount(payloadMinimumAmount)
      ? payloadMinimumAmount
      : requestMinimumAmount;

    if (this.hasMinimumAmount(minimumAmount)) {
      const formattedMinimumAmount = this.formatCurrency(minimumAmount);

      return `The insurance amount you entered is below the minimum required for this commodity. Please enter an amount of at least ${formattedMinimumAmount} to proceed.`;
    }

    return (
      errorPayload?.errorMessage ||
      'The insurance amount you entered is below the minimum required for this commodity.'
    );
  }

  private getInsuranceErrorCode(error: HttpErrorResponse): string {
    const payload = this.getInsuranceErrorPayload(error);

    return payload?.errorCode ?? payload?.code ?? payload?.error_code ?? '';
  }

  private isMinimumInsuranceAmountError(
    error: HttpErrorResponse,
    url: string
  ): boolean {
    const errorCode = this.getInsuranceErrorCode(error);

    if (!INSURANCE_MINIMUM_AMOUNT_ERROR_CODES.includes(errorCode)) {
      return false;
    }

    if (!this.isInsuranceEndpoint(url)) {
      return false;
    }

    if (errorCode !== INSURANCE_MINIMUM_AMOUNT_ERROR_CODE) {
      return true;
    }

    return this.isInsuranceMinimumAmountMessage(error);
  }

  private isInsuranceMinimumAmountMessage(error: HttpErrorResponse): boolean {
    const errorPayload = this.getInsuranceErrorPayload(error);
    const message =
      `${errorPayload?.errorMessage ?? errorPayload?.message ?? ''}`
        .trim()
        .toLowerCase();

    return (
      message.includes('insurance amount') &&
      message.includes('minimum required')
    );
  }

  private getInsuranceErrorPayload(error: HttpErrorResponse): any {
    const payload = error?.error;

    if (!payload || typeof payload !== 'object') {
      return {};
    }

    if (payload.error && typeof payload.error === 'object') {
      return {
        ...payload,
        ...payload.error,
      };
    }

    if (Array.isArray(payload.errors) && payload.errors[0]) {
      return {
        ...payload,
        ...payload.errors[0],
      };
    }

    return payload;
  }

  private getMinimumAmountFromPayload(payload: any): unknown {
    return (
      payload?.minimumAmount ??
      payload?.minimum_amount ??
      payload?.minAmount ??
      payload?.min_amount
    );
  }

  private hasMinimumAmount(value: unknown): boolean {
    if (value === null || value === undefined || value === '') {
      return false;
    }

    return Number.isFinite(Number(value));
  }

  private getMinimumAmountFromRequest(
    req: HttpRequest<any>,
    callback: (minimumAmount?: unknown) => void
  ): void {
    if (req.body instanceof FormData) {
      const jsonBlob = getJsonBlobFromFormData(req.body);

      if (!jsonBlob) {
        callback();
        return;
      }

      readJsonFromBlob(
        jsonBlob,
        (requestDto) => {
          callback(this.getCachedMinimumAmountForRequestBody(requestDto));
        },
        () => callback()
      );
      return;
    }

    callback(this.getCachedMinimumAmountForRequestBody(req.body));
  }

  private getCachedMinimumAmountForRequestBody(body: any): number | null {
    const candidates = [
      body?.insurance,
      body?.returnLabel?.insurance,
      body?.returnInsurance,
    ].filter((insurance) => insurance?.commodityClientCode);

    for (const candidate of candidates) {
      const minimumAmount =
        this.contentManagementService.getCachedInsuranceCommodityMinimumAmount(
          candidate.commodityClientCode
        );

      if (
        this.hasMinimumAmount(minimumAmount) &&
        this.hasMinimumAmount(candidate.insuranceAmount) &&
        Number(candidate.insuranceAmount) < Number(minimumAmount)
      ) {
        return minimumAmount;
      }
    }

    const firstCandidate = candidates[0];

    return firstCandidate
      ? this.contentManagementService.getCachedInsuranceCommodityMinimumAmount(
          firstCandidate.commodityClientCode
        )
      : null;
  }

  private formatCurrency(value: unknown): string {
    const amount = Number(value);

    if (!Number.isFinite(amount)) {
      return '$0.00';
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  private removeInsuranceFromRequestBody(body: any): void {
    if (!body || typeof body !== 'object') {
      return;
    }

    if (body.insurance) {
      delete body.insurance;
    }

    if (body.returnLabel?.insurance) {
      body.returnLabel = { ...body.returnLabel };
      delete body.returnLabel.insurance;
    }

    if (body.returnInsurance) {
      delete body.returnInsurance;
    }
  }
}
